import toast from 'react-hot-toast';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Paper,
  IconButton,
  Button,
  InputAdornment,
  Divider,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  GlobalStyles,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { debounce } from 'lodash';

import {
  editPatient,
  deletePatient,
  assignPatient,
  getAssignedPatients,
  getAssignees
} from 'src/redux/patient/patient.action';
import { AssignPatientModal } from './assignPatientModal';

import CustomAvatar from 'src/@core/components/mui/avatar';
import { getInitials } from 'src/@core/utils/get-initials';

/* ========================= Design Tokens ========================= */

const T = {
  crimson:    '#B01030',
  crimsonDk:  '#8A0D25',
  crimsonLt:  '#FCF0F2',
  crimsonMid: '#E8C0C8',
  slate900:   '#0D1B2A',
  slate700:   '#2C3E50',
  slate500:   '#5A6A7A',
  slate300:   '#A8B8C8',
  slate100:   '#EEF2F6',
  slate50:    '#F7F9FB',
  white:      '#FFFFFF',
  green:      '#0D8A5E',
  greenBg:    '#E8FAF4',
  amber:      '#C07A10',
  amberBg:    '#FDF6E8',
  orange:     '#C05818',
  orangeBg:   '#FDF0E8',
  red:        '#C01828',
  redBg:      '#FDE8EA',
  muted:      '#8A9BAC',
  mutedBg:    '#F0F4F8',
  border:     '#E2E8EF',
  shadow:     'rgba(13,27,42,0.07)',
};

/* ========================= MUI Theme ========================= */

const theme = createTheme({
  typography: {
    fontFamily: '"DM Sans", "Outfit", system-ui, sans-serif',
  },
  palette: {
    primary: { main: T.crimson },
    background: { default: T.slate50 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, letterSpacing: '-0.01em' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: '0 24px 60px rgba(13,27,42,0.18)' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: T.slate900, fontSize: '0.72rem', borderRadius: 6 },
        arrow: { color: T.slate900 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: '0.85rem', borderRadius: 6, margin: '2px 4px' },
      },
    },
  },
});

/* ========================= Global Font ========================= */

const globalStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
  `} />
);

/* ========================= Styled Components ========================= */

const TableCard = styled(Paper)(() => ({
  borderRadius: '0 0 18px 18px',
  border: `1px solid ${T.border}`,
  borderTop: 'none',
  backgroundColor: T.white,
  boxShadow: `0 2px 20px ${T.shadow}`,
  overflow: 'hidden',
}));

const SearchField = styled(TextField)(() => ({
  width: 296,
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    height: 40,
    backgroundColor: T.slate50,
    fontSize: '0.855rem',
    fontFamily: '"DM Sans", system-ui, sans-serif',
    '& fieldset': { borderColor: T.border },
    '&:hover fieldset': { borderColor: T.slate300 },
    '&.Mui-focused fieldset': { borderColor: T.crimson, borderWidth: '1.5px' },
  },
  '& input::placeholder': { color: T.muted, opacity: 1 },
}));

const statusMap = {
  scheduled:      { bg: T.greenBg,  text: T.green,  dot: T.green,  label: 'Scheduled'    },
  'no-answer':    { bg: T.amberBg,  text: T.amber,  dot: T.amber,  label: 'No Answer'    },
  'wrong-number': { bg: T.orangeBg, text: T.orange, dot: T.orange, label: 'Wrong Number' },
  refused:        { bg: T.redBg,    text: T.red,    dot: T.red,    label: 'Refused'      },
  'no-status':    { bg: T.mutedBg,  text: T.muted,  dot: T.muted,  label: 'No Status'    },
};

const StatusBadge = ({ status }) => {
  const cfg = statusMap[status] || statusMap['no-status'];
  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      backgroundColor: cfg.bg,
      color: cfg.text,
      px: '9px',
      py: '3px',
      borderRadius: '20px',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.03em',
      whiteSpace: 'nowrap',
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
};

const filterPillConfig = {
  all:            { color: T.crimson, bg: T.crimsonLt, border: T.crimsonMid },
  scheduled:      { color: T.green,  bg: T.greenBg,   border: '#A7E8D0'    },
  'no-answer':    { color: T.amber,  bg: T.amberBg,   border: '#F0D090'    },
  'wrong-number': { color: T.orange, bg: T.orangeBg,  border: '#F0C0A0'    },
  refused:        { color: T.red,    bg: T.redBg,     border: '#F0A0A8'    },
  'no-status':    { color: T.muted,  bg: T.mutedBg,   border: '#C8D8E8'    },
};

const FilterPill = ({ children, active, statuskey, onClick }) => {
  const cfg = filterPillConfig[statuskey] || filterPillConfig['all'];
  return (
    <Box onClick={onClick} sx={{
      padding: '5px 13px',
      cursor: 'pointer',
      userSelect: 'none',
      color: active ? cfg.color : T.slate500,
      fontWeight: active ? 700 : 500,
      fontSize: '0.72rem',
      letterSpacing: '0.03em',
      borderRadius: 20,
      backgroundColor: active ? cfg.bg : 'transparent',
      border: `1.5px solid ${active ? cfg.border : 'transparent'}`,
      transition: 'all 0.14s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      '&:hover': { color: cfg.color, backgroundColor: cfg.bg, border: `1.5px solid ${cfg.border}` },
    }}>
      {active && <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: cfg.color, flexShrink: 0 }} />}
      {children}
    </Box>
  );
};

const ActionBtn = styled(IconButton)(({ actiontype }) => ({
  padding: 6,
  borderRadius: 8,
  transition: 'all 0.14s ease',
  '& svg': {
    fontSize: 16,
    color: actiontype === 'delete' ? T.red : actiontype === 'assign' ? '#6D3FC4' : T.slate500,
  },
  '&:hover': {
    backgroundColor: actiontype === 'delete' ? T.redBg : actiontype === 'assign' ? '#F0EBF9' : T.slate100,
    transform: 'scale(1.1)',
  },
}));

const avatarPalette = [T.crimson, '#6D3FC4', '#0A6E8A', T.green, T.amber, T.orange];
const getAvatarColor = (name = '') => avatarPalette[(name?.charCodeAt(0) || 0) % avatarPalette.length];

const PatientAvatar = ({ row }) => {
  const initials = `${row.firstName?.charAt(0) || ''}${row.lastName?.charAt(0) || ''}`.toUpperCase();
  const color = getAvatarColor(row.firstName);
  return (
    <Avatar sx={{
      width: 36,
      height: 36,
      fontSize: '0.78rem',
      fontWeight: 700,
      flexShrink: 0,
      backgroundColor: color + '1A',
      color,
      border: `1.5px solid ${color}40`,
    }}>
      {initials || '?'}
    </Avatar>
  );
};

const editFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    fontSize: '0.875rem',
    '&.Mui-focused fieldset': { borderColor: T.crimson },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: T.crimson },
  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
};

/* ============================== Helpers ============================== */

const normalizeStatus = (status) => {
  if (status === null || status === undefined || status === '') return 'no-status';
  return status;
};

const getTimeRange = (timeFilter) => {
  const now = new Date();
  const today = new Date(now);
  switch (timeFilter) {
    case 'recent': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: today };
    }
    case 'past-week': {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { start: weekAgo, end: today };
    }
    case 'past-month': {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      return { start: monthAgo, end: today };
    }
    default:
      return { start: null, end: null };
  }
};

const useCurrentUserId = () => {
  return useSelector((s) =>
    s.auth?.user?._id ??
    s.auth?.userData?._id ??
    s.auth?.user?.id ??
    s.auth?.userData?.id ??
    (() => {
      try {
        const raw = localStorage.getItem('userData') || localStorage.getItem('user');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?._id ?? parsed?.id ?? null;
      } catch {
        return null;
      }
    })()
  );
};

const getAssigneeIdsFromRow = (row) => {
  if (!row?.assignee) return [];

  if (typeof row.assignee === 'string') {
    return [row.assignee];
  }

  if (typeof row.assignee === 'object') {
    return [row.assignee._id || row.assignee.id].filter(Boolean);
  }

  return [];
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

/* ============================ Main Component ============================ */

export const AssignedPatientTable = () => {
  const dispatch = useDispatch();

  // const {
  //   assignedPatients,
  //   isAssignedPatientsLoading,
  //   isAssignedPatientsLoadingFailed,
  //   isAssigningPatient,
  //   assignPatientError,
  // } = useSelector((state) => state.patient ?? state.patients);

const {
  assignedPatients,
  assignees,
  isAssignedPatientsLoading,
  isAssignedPatientsLoaded,
  isAssignedPatientsLoadingFailed,
  isAssigningPatient,
  assignPatientError,
} = useSelector(state => state.patient);

  // const currentUserId = useCurrentUserId();
// Find where currentUserId is set, something like:
const currentUserId = useCurrentUserId();

// Add a useEffect to verify it's set:
useEffect(() => {
  console.log('Current user ID:', currentUserId);
  if (!currentUserId) {
    console.warn('Warning: currentUserId is not set. Assignment may fail.');
  }
}, [currentUserId]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isAssignPatientModal, setAssignPatientModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);

  

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchQuery(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500),
    []
  );
  //another useEffect to refetch patients after successful assignment


// ADD THIS NEW useEffect RIGHT AFTER:
// useEffect(() => {
//   // When assignment completes, refresh the list
//   if (!isAssigningPatient && assignPatientError === null) {
//     console.log('Assignment detected, refreshing assigned patients');
//     dispatch(getAssignedPatients());
//   }
// }, [isAssigningPatient, assignPatientError, dispatch]);

useEffect(() => {
  console.log('🚀 Fetching assigned patients + assignees');

  dispatch(getAssignedPatients());
  dispatch(getAssignees());

}, [dispatch]);
 
// ✅ FIX 2: Debug current state
useEffect(() => {
  console.log('📊 Current State:', {
    assignedPatients: assignedPatients?.length || 0,
    currentUserId,
    isLoading: isAssignedPatientsLoading,
    isLoaded: isAssignedPatientsLoaded,
    hasFailed: isAssignedPatientsLoadingFailed
  });
}, [assignedPatients, currentUserId, isAssignedPatientsLoading, isAssignedPatientsLoaded, isAssignedPatientsLoadingFailed]);


  const handleAssignClick = (patient) => {
  setSelectedPatient(patient);
  setAssignPatientModal(true);
};

  const handleEditClick = (patient) => {
    setSelectedPatient(patient);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (patient) => {
    setSelectedPatient(patient);
    setDeleteModalOpen(true);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleTimeFilterChange = (newTimeFilter) => {
    setTimeFilter(newTimeFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

// ✅ REFRESH FUNCTION - Fetches latest data
const refresh = () => {
  console.log('🔄 Refreshing assigned patients list');
  dispatch(getAssignedPatients());
};

const handleEditSave = async () => {
  if (!selectedPatient?._id) {
    toast.error('Patient not found');
    return;
  }

  try {
    // ✅ Ensure status is included in the update
    const updatePayload = {
      firstName: selectedPatient.firstName,
      lastName: selectedPatient.lastName,
      email: selectedPatient.email,
      phoneNumber: selectedPatient.phoneNumber,
      cinNumber: selectedPatient.cinNumber,
      status: selectedPatient.status || '',  // ✅ Include status
      comment: selectedPatient.comment || ''
    };

    console.log('Saving patient with payload:', updatePayload);

    await dispatch(editPatient({
      id: selectedPatient._id,
      data: updatePayload
    }));

    setEditModalOpen(false);
    setSelectedPatient(null);
    refresh();  // ✅ REFRESH DATA AFTER SAVE - INSTANT UPDATE!
    // toast.success('Patient updated successfully');
  } catch (error) {
    console.error('Error saving patient:', error);
    toast.error('Failed to update patient');
  }
};

  const handleDeleteConfirm = async () => {
    if (selectedPatient?._id) {
      try {
        await dispatch(deletePatient(selectedPatient._id)).unwrap();
        setDeleteModalOpen(false);
        refresh();  // ✅ Use refresh function for consistency
        toast.success('Patient deleted successfully');
      } catch (error) {
        console.error('Error deleting patient:', error);
        toast.error('Failed to delete patient');
      }
    }
  };

  // const handleAssignToMe = async () => {
  //   if (!selectedPatient?._id) {
  //     toast.error('Please select a patient first');
  //     return;
  //   }

  //   if (!currentUserId) {
  //     toast.error('Unable to identify current user');
  //     return;
  //   }

  //   try {
  //     await dispatch(
  //       assignPatient({
  //         id: selectedPatient._id,
  //         assigneeId: currentUserId,
  //       })
  //     ).unwrap();

  //     setAssignModalOpen(false);
  //     setSelectedPatient(null);
  //     toast.success('Patient assigned to you');
  //   } catch (error) {
  //     console.error('Error assigning patient:', error);
  //     toast.error('Failed to assign patient');
  //   }
  // };


const handleAssignPatient = (assignee) => {
  const resolvedId = assignee?._id ?? assignee?.id;

  if (!selectedPatient?._id || !resolvedId) {
    toast.error('Invalid patient or assignee');
    console.error('Assignee object received:', assignee); // ← tells you the real shape
    return;
  }

  dispatch(assignPatient({
    id: selectedPatient._id,
    assigneeId: resolvedId,
  })).then(() => {
    setSelectedPatient(null);
    setAssignPatientModal(false);
    refresh();  // ✅ REFRESH DATA AFTER ASSIGNMENT - INSTANT UPDATE!
    toast.success('Patient assigned successfully');
  }).catch((error) => {
    console.error('Error assigning patient:', error);
    toast.error(error?.message || 'Failed to assign patient');
  });
};
  const handleFilterClick = (event) => setAnchorEl(event.currentTarget);
  const handleFilterClose = () => setAnchorEl(null);

  /* ===================== FILTERS ===================== */

  const { start, end } = getTimeRange(timeFilter);

//   const assignedToMeOnly = (assignedPatients || []).filter((p) => {
//   if (!currentUserId) return false;
//   const ids = getAssigneeIdsFromRow(p).map(String);
//   return ids.includes(String(currentUserId));
// });

const assignedToMeOnly = (assignedPatients || []).filter((p) => {
  const assigneeId =
    typeof p.assignee === 'object'
      ? p.assignee?._id
      : p.assignee;

  return String(assigneeId) === String(currentUserId);
});
useEffect(() => {
  console.log('Sample assigned patient:', assignedPatients?.[0]);
}, [assignedPatients]);
  // NEW: Shows only YOUR patients
const showMineOnly = true;
// const filteredPatients = useMemo((showMineOnly) => {
//   const q = searchQuery.trim().toLowerCase();
//   const list = Array.isArray(assignedPatients) ? assignedPatients : [];

//   const myId = String(currentUserId);

//   return list
//     .filter((p) => getAssigneeIdsFromRow(p).length > 0)

//     // ✅ FIXED FILTER
//     .filter((p) => {
//       if (!showMineOnly) return true;

//       const ids = getAssigneeIdsFromRow(p).map(String);
//       return myId && ids.includes(myId);
//     })

//     .filter((p) => {
//       if (q) {
//         const fullName = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
//         const email = (p.email || '').toLowerCase();
//         if (!fullName.includes(q) && !email.includes(q)) return false;
//       }

//       const displayStatus = normalizeStatus(p.status);
//       if (statusFilter !== 'all' && displayStatus !== statusFilter) return false;

//       if (start && end) {
//         const raw = p.updatedAt || p.createdAt;
//         if (!raw) return false;
//         const ts = new Date(raw).getTime();
//         return ts >= start.getTime() && ts <= end.getTime();
//       }

//       return true;
//     });

// }, [assignedPatients, showMineOnly, currentUserId, searchQuery, statusFilter, start, end]);

  const filteredPatients = assignedToMeOnly.filter((patient) => {
    const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      q === '' ||
      fullName.toLowerCase().includes(q) ||
      (patient.email || '').toLowerCase().includes(q);

    const displayStatus = normalizeStatus(patient.status);
    const matchesStatus = statusFilter === 'all' ? true : displayStatus === statusFilter;

    if (!start || !end) return matchesSearch && matchesStatus;

    const rawDate = patient.updatedAt || patient.createdAt;
    if (!rawDate) return false;

    const ts = new Date(rawDate);
    const matchesTime = ts >= start && ts <= end;

    return matchesSearch && matchesStatus && matchesTime;
  })

  .sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return dateB - dateA;
  });
  /* =============================== Columns =============================== */

  const columns = [
    {
      flex: 0.22,
      minWidth: 210,
      field: 'user',
      headerName: 'Patient',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <PatientAvatar row={row} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color={T.slate900}
              sx={{
                lineHeight: 1.35,
                whiteSpace: 'nowrap',
                fontSize: '0.855rem',
              }}>
              {`${row.firstName || ''} ${row.lastName || ''}`}
            </Typography>
            <Typography sx={{ color: T.muted, fontSize: '0.72rem', lineHeight: 1 }}>
              {row.email || '—'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      flex: 0.1,
      minWidth: 118,
      field: 'facility',
      headerName: 'Facility',
      renderCell: ({ row }) => {
        const label =
          row.facility === 'visiting-docs'
            ? 'Visiting Docs'
            : row.facility === 'ras-med'
            ? 'Ras Med'
            : row.facility || null;
        return label ? (
          <Chip
            label={label}
            size="small"
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              height: 22,
              borderRadius: 6,
              backgroundColor: '#EAF3FF',
              color: '#1A5FB4',
              border: 'none',
            }}
          />
        ) : (
          <Typography sx={{ color: T.muted, fontSize: '0.82rem' }}>—</Typography>
        );
      },
    },
    {
      flex: 0.11,
      minWidth: 128,
      field: 'phoneNumber',
      headerName: 'Phone',
      renderCell: ({ row }) => (
        <Typography sx={{ color: T.slate700, fontSize: '0.82rem', fontFamily: '"DM Mono", monospace' }}>
          {row.phoneNumber || '—'}
        </Typography>
      ),
    },
    {
      flex: 0.09,
      minWidth: 100,
      field: 'hasInsurance',
      headerName: 'Insurance',
      renderCell: ({ row }) => {
        const has = row.hasInsurance === 'yes' || row.hasInsurance === 'Yes';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon
              icon={has ? 'solar:shield-check-bold' : 'solar:shield-cross-bold'}
              style={{ fontSize: 16, color: has ? T.green : T.muted }}
            />
            <Typography
              sx={{
                fontSize: '0.82rem',
                fontWeight: has ? 600 : 400,
                color: has ? T.green : T.muted,
              }}>
              {has ? 'Yes' : 'No'}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 118,
      field: 'status',
      headerName: 'Status',
      renderCell: ({ row }) => <StatusBadge status={normalizeStatus(row.status)} />,
    },
    {
      flex: 0.09,
      minWidth: 105,
      field: 'dob',
      headerName: 'Date of Birth',
      renderCell: ({ row }) => (
        <Typography sx={{ color: T.slate700, fontSize: '0.82rem' }}>
          {formatDate(row.dob)}
        </Typography>
      ),
    },
    {
      flex: 0.08,
      minWidth: 98,
      field: 'cinNumber',
      headerName: 'CIN',
      renderCell: ({ row }) => (
        <Typography
          sx={{
            color: T.slate700,
            fontSize: '0.8rem',
            fontFamily: '"DM Mono", monospace',
            letterSpacing: '0.02em',
          }}>
          {row.cinNumber || '—'}
        </Typography>
      ),
    },
    {
      flex: 0.13,
      minWidth: 148,
      field: 'comment',
      headerName: 'Comment',
      renderCell: ({ row }) => (
        <Tooltip title={row.comment || ''} placement="top" arrow>
          <Typography
            sx={{
              color: T.slate500,
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 140,
            }}>
            {row.comment || '—'}
          </Typography>
        </Tooltip>
      ),
    },
  
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.09,
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="Assign" arrow>
            <ActionBtn size="small" actiontype="assign" onClick={() => handleAssignClick(row)}>
              <Icon icon="solar:user-plus-bold" />
            </ActionBtn>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <ActionBtn size="small" actiontype="edit" onClick={() => handleEditClick(row)}>
              <Icon icon="solar:pen-bold" />
            </ActionBtn>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <ActionBtn size="small" actiontype="delete" onClick={() => handleDeleteClick(row)}>
              <Icon icon="solar:trash-bin-minimalistic-bold" />
            </ActionBtn>
          </Tooltip>
        </Box>
      ),
    },
  ];

  /* ============================== UI States ============================== */

  if (isAssignedPatientsLoading) {
    return (
      <TableCard>
        <Box sx={{ height: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
          <CircularProgress size={34} sx={{ color: T.crimson }} />
          <Typography sx={{ color: T.muted, fontSize: '0.82rem' }}>Loading assigned patients…</Typography>
        </Box>
      </TableCard>
    );
  }

  if (isAssignedPatientsLoadingFailed) {
    return (
      <TableCard>
        <Box sx={{ height: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Icon icon="solar:danger-triangle-bold" style={{ fontSize: 38, color: T.red }} />
          <Typography sx={{ color: T.red, fontSize: '0.85rem' }}>Failed to load assigned patients</Typography>
        </Box>
      </TableCard>
    );
  }
console.log('ASSIGNEES FROM REDUX:', assignees);
  /* ============================== Render ============================== */

  return (
    <ThemeProvider theme={theme}>
      {globalStyles}
      <TableCard>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: `1px solid ${T.border}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: '1.28rem',
                  fontWeight: 700,
                  color: T.slate900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.2,
                }}>
                Assigned Patients
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: T.muted, mt: 0.35, fontWeight: 400 }}>
                View and manage patients assigned to you
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: `1.5px solid ${T.crimsonMid}`,
                  borderRadius: 10,
                  px: 1.75,
                  py: 0.9,
                  backgroundColor: T.crimsonLt,
                }}>
                <Icon icon="solar:users-group-rounded-bold" style={{ color: T.crimson, fontSize: 18 }} />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: T.crimson,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      lineHeight: 1,
                    }}>
                    Total Assigned
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: T.crimson, lineHeight: 1.15 }}>
                    {filteredPatients.length}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Toolbar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <SearchField
              placeholder="Search by name, email, CIN…"
              variant="outlined"
              size="small"
              onChange={(e) => debouncedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="solar:magnifer-bold" style={{ color: T.muted, fontSize: 17 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {['all', 'scheduled', 'no-answer', 'wrong-number', 'refused', 'no-status'].map((s) => (
                  <FilterPill
                    key={s}
                    active={statusFilter === s}
                    statuskey={s}
                    onClick={() => {
                      handleStatusFilterChange(s);
                    }}>
                    {s === 'all'
                      ? 'All'
                      : s === 'no-status'
                      ? 'No Status'
                      : s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </FilterPill>
                ))}
              </Box>

              <Box sx={{ width: '1px', height: 22, backgroundColor: T.border, mx: 0.5 }} />

              <Button
                onClick={handleFilterClick}
                endIcon={<Icon icon="solar:alt-arrow-down-bold" style={{ fontSize: 13 }} />}
                sx={{
                  color: T.slate500,
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  border: `1.5px solid ${T.border}`,
                  borderRadius: 8,
                  px: 1.5,
                  height: 34,
                  '&:hover': { borderColor: T.crimson, color: T.crimson, backgroundColor: T.crimsonLt },
                }}>
                <Icon icon="solar:calendar-line-duotone" style={{ fontSize: 15, marginRight: 5 }} />
                {timeFilter === 'all' ? 'All Time' : timeFilter === 'recent' ? 'Last 24h' : timeFilter.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
                PaperProps={{
                  sx: {
                    borderRadius: 12,
                    boxShadow: '0 10px 36px rgba(13,27,42,0.14)',
                    mt: 0.5,
                    minWidth: 148,
                  },
                }}>
                {[
                  { value: 'all', label: 'All Time' },
                  { value: 'recent', label: 'Last 24h' },
                  { value: 'past-week', label: 'Past Week' },
                  { value: 'past-month', label: 'Past Month' },
                ].map((f) => (
                  <MenuItem
                    key={f.value}
                    selected={timeFilter === f.value}
                    onClick={() => {
                      handleTimeFilterChange(f.value);
                      handleFilterClose();
                    }}
                    sx={{
                      fontSize: '0.82rem',
                      '&.Mui-selected': {
                        backgroundColor: T.crimsonLt,
                        color: T.crimson,
                        fontWeight: 700,
                      },
                    }}>
                    {f.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>

        {/* Grid */}
        <DataGrid
          autoHeight
          rows={filteredPatients}
          columns={columns}
          rowCount={filteredPatients.length}
          loading={isAssignedPatientsLoading}
          paginationMode="client"
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row._id}
          disableRowSelectionOnClick
          rowHeight={64}
          sx={{
            border: 'none',
            fontFamily: '"DM Sans", system-ui, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: T.slate50,
              borderBottom: `1px solid ${T.border}`,
              minHeight: '42px !important',
              maxHeight: '42px !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.67rem',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: T.muted,
            },
            '& .MuiDataGrid-columnSeparator': { display: 'none' },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${T.slate100}`,
              outline: 'none !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row': {
              transition: 'background 0.1s',
              '&:hover': { backgroundColor: '#FAFBFC' },
              '&:last-child .MuiDataGrid-cell': { borderBottom: 'none' },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${T.slate100}`,
              backgroundColor: T.slate50,
              minHeight: 50,
            },
            '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.78rem',
              color: T.slate500,
              fontFamily: '"DM Sans", system-ui',
            },
            '& .MuiDataGrid-virtualScroller': { minHeight: 200 },
          }}
        />
      </TableCard>

      {/* Edit Dialog */}
      <Dialog open={isEditModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${T.crimson}, ${T.crimsonDk})`,
            color: T.white,
            py: 2.25,
            px: 3,
            fontWeight: 700,
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
          <Icon icon="solar:pen-bold" style={{ fontSize: 17 }} />
          Edit Patient
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.5 }}>
            <TextField
              label="First Name"
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPatient?.firstName || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, firstName: e.target.value })}
              sx={editFieldSx}
            />
            <TextField
              label="Last Name"
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPatient?.lastName || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, lastName: e.target.value })}
              sx={editFieldSx}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPatient?.email || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, email: e.target.value })}
              sx={{ ...editFieldSx, gridColumn: 'span 2' }}
            />
            <TextField
              label="Phone Number"
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPatient?.phoneNumber || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, phoneNumber: e.target.value })}
              sx={editFieldSx}
            />
            <TextField
              label="CIN Number"
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPatient?.cinNumber || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, cinNumber: e.target.value })}
              sx={editFieldSx}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: T.muted,
                mb: 0.75,
              }}>
              Status
            </Typography>
            <RadioGroup
              row
              value={selectedPatient?.status || ''}
              onChange={(e) => setSelectedPatient({ ...selectedPatient, status: e.target.value })}
              sx={{ gap: 0.25 }}>
              {[
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'no-answer', label: 'No Answer' },
                { value: 'wrong-number', label: 'Wrong Number' },
                { value: 'refused', label: 'Refused' },
                { value: '', label: 'No Status' },
              ].map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: T.border,
                        '&.Mui-checked': { color: T.crimson },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.82rem', color: T.slate700 }}>{opt.label}</Typography>}
                />
              ))}
            </RadioGroup>
          </Box>

          <TextField
            label="Comment"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            size="small"
            value={selectedPatient?.comment || ''}
            onChange={(e) => setSelectedPatient({ ...selectedPatient, comment: e.target.value })}
            sx={{ ...editFieldSx, mt: 2.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, gap: 1 }}>
          <Button onClick={() => setEditModalOpen(false)} sx={{ color: T.slate500, borderRadius: 8, fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            sx={{
              backgroundColor: T.crimson,
              borderRadius: 8,
              fontWeight: 700,
              px: 3,
              boxShadow: `0 4px 14px ${T.crimson}35`,
              '&:hover': { backgroundColor: T.crimsonDk },
            }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 4, pb: 2, px: 3, textAlign: 'center' }}>
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              backgroundColor: T.redBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
            <Icon icon="solar:trash-bin-minimalistic-bold" style={{ fontSize: 26, color: T.red }} />
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: T.slate900, mb: 0.75 }}>
            Delete Patient?
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: T.slate500, lineHeight: 1.55 }}>
            This action cannot be undone. The patient record will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'center' }}>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            variant="outlined"
            sx={{
              borderColor: T.border,
              color: T.slate500,
              borderRadius: 8,
              fontWeight: 500,
              px: 3,
              '&:hover': { borderColor: T.slate300, backgroundColor: T.slate50 },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: T.red,
              borderRadius: 8,
              fontWeight: 700,
              px: 3,
              boxShadow: `0 4px 14px ${T.red}35`,
              '&:hover': { backgroundColor: '#A01020' },
            }}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to Me Dialog */}
      {/* <Dialog open={isAssignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${T.crimson}, ${T.crimsonDk})`,
            color: T.white,
            py: 2.25,
            px: 3,
            fontWeight: 700,
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
          <Icon icon="solar:user-plus-bold" style={{ fontSize: 17 }} />
          Assign Patient
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3 }}>
          <Typography sx={{ fontSize: '0.9rem', color: T.slate700, mb: 2 }}>
            Assign{' '}
            <span style={{ fontWeight: 700 }}>
              {selectedPatient?.firstName} {selectedPatient?.lastName}
            </span>{' '}
            to yourself?
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 12,
              backgroundColor: T.crimsonLt,
              border: `1.5px solid ${T.crimsonMid}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}>
            <Icon icon="solar:user-check-rounded-bold" style={{ fontSize: 20, color: T.crimson }} />
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: T.crimson, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Assign to You
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: T.slate700, mt: 0.25 }}>
                This patient will be added to your assigned patients list
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, gap: 1 }}>
          <Button
            onClick={() => setAssignModalOpen(false)}
            sx={{ color: T.slate500, borderRadius: 8, fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignToMe}
            variant="contained"
            disabled={isAssigningPatient}
            sx={{
              backgroundColor: T.crimson,
              borderRadius: 8,
              fontWeight: 700,
              px: 3,
              boxShadow: `0 4px 14px ${T.crimson}35`,
              '&:hover': { backgroundColor: T.crimsonDk },
            }}>
            {isAssigningPatient ? 'Assigning...' : 'Assign to Me'}
          </Button>
        </DialogActions>
      </Dialog> */}
      <AssignPatientModal
  open={isAssignPatientModal}
  onClose={() => setAssignPatientModal(false)}
  patient={selectedPatient}
  handleAssignPatient={handleAssignPatient}
  assignees={assignees}
/>
    </ThemeProvider>
  );
};